import React, { Fragment, useCallback, useEffect, useMemo, useState, useRef } from "react";
import { connect } from "react-redux";
import ContentFeature from "Components/ContentFeature";
import ReadMoreText from "Components/ReadMoreText";
import { FaDownload, FaRegMinusSquare, FaRegPlusSquare } from "react-icons/fa";
import { venueInteraction } from "../../modules/analytics";
import { isAgency, isCorporate, isRolePlatformAdmin } from "../../modules/auth";
import { getYouTubeEmbedUrl } from "../../utils";
import { ButtonBrand } from "../../Components/Button";
import axios from "axios";
import * as VENUE_ROUTES from "../../Router/venue_routes";
import { isCompanyAdmin, isCompanyUser } from "../../modules/permissions";
import { VENUE_TAGS } from "../../modules/analytics/tags";
import { useOnScreen } from "../../hooks/useOnScreen";

const RepresentationCompanies = ({
                                   anchor,
                                   mobile,
                                   venueSlug,
                                   isAgency,
                                   isCorporate,
                                   isRolePlatformAdmin,
                                   venueInteraction,
                                   venueId,
                                   dataScore,
                                   impressionId,
                                   setRepresentationCompanyLogos = () => {},
                                 }) => {
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [representationCompanies, setRepresentationCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasBeenOnScreenCompanyRef = useRef(false);
  const companyRef = useRef();
  const onScreen = useOnScreen(companyRef, "0px");
  useEffect(() => {
    if (onScreen && !hasBeenOnScreenCompanyRef.current) {
      hasBeenOnScreenCompanyRef.current = true;
      representationCompanies.forEach((companies) => {
        venueInteraction({
          type: VENUE_TAGS.VENUE_GROUP_CONTENT_VIEWED,
          data_score: dataScore,
          impression_id: impressionId,
          id:venueId,
        });
      })
    }
  }, [onScreen]);



  const hasAgencyAccess = useMemo(() =>
      isAgency || isRolePlatformAdmin || isCorporate ,
    [isAgency, isRolePlatformAdmin],
  );

  const fetchRepresentationCompaniesForVenue = useCallback(async () => {
    if (!venueSlug) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const apiUrl = window.API + VENUE_ROUTES.API.VENUE.REPRESENTATION_COMPANIES.replace(":venue", venueSlug);
      const response = await axios.get(apiUrl);

      if (response.data && Array.isArray(response.data.data)) {
        setRepresentationCompanies(response.data.data);
        const logos = response.data.data
          .filter(company => {
            const brandDescription = company.description || "";
            const agencyProfile = hasAgencyAccess ? company.agency_profile : null;
            const hotelBrandVideo = hasAgencyAccess ? company.account_manager_video : null;
            const hotelBrandVideoCorporate = hasAgencyAccess ? company?.account_manager_video_corporate : null;
            const hasContent = brandDescription || agencyProfile || hotelBrandVideo || hotelBrandVideoCorporate;
            return hasContent && company.cdn_path;
          })
          .map(company => ({
            id: company.id,
            name: company.name,
            logo: `${process.env.REACT_APP_API}/api/hotel-groups/${company.id}/image`
          }));

        setRepresentationCompanyLogos(logos);
      } else {
        setRepresentationCompanies([]);
      }
    } catch (err) {
      console.error("Failed to fetch representation companies:", err);
      setError(err.response?.data?.message || "Failed to load representation companies");
      setRepresentationCompanies([]);
    } finally {
      setLoading(false);
    }
  }, [venueSlug]);

  useEffect(() => {
    fetchRepresentationCompaniesForVenue();
  }, [fetchRepresentationCompaniesForVenue]);

  const toggleExpanded = useCallback((index) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  }, []);

  const validCompanies = useMemo(() =>
      representationCompanies.filter(company => {
        const brandDescription = company.description || "";
        const agencyProfile = hasAgencyAccess ? company.agency_profile : null;
        const hotelBrandVideo = hasAgencyAccess ? company.account_manager_video : null;
        const hotelBrandVideoCorporate = hasAgencyAccess ? company?.account_manager_video_corporate : null;
        return brandDescription || agencyProfile || hotelBrandVideo || hotelBrandVideoCorporate;
      }),
    [representationCompanies, hasAgencyAccess],
  );

  if (loading) {
    return (
      <div className="bg-white text-lg mb-3 shadow p-4" ref={companyRef}>
        <div className="animate-pulse">
          <div className="h-6 bg-gray-300 rounded mb-4 w-3/4"></div>
          <div className="h-4 bg-gray-300 rounded mb-2"></div>
          <div className="h-4 bg-gray-300 rounded mb-2 w-5/6"></div>
          <div className="h-4 bg-gray-300 rounded w-4/6"></div>
        </div>
      </div>
    );
  }

  if (error && hasAgencyAccess) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded mb-3" ref={companyRef}>
        <p className="font-medium">Error loading representation companies</p>
        <button
          onClick={fetchRepresentationCompaniesForVenue}
          className="mt-2 text-sm underline hover:no-underline"
        >
          Try again
        </button>
      </div>
    );
  }

  let displayCompanies = () => {
    return validCompanies.map((company, idx) => {
      const isExpanded = expandedItems.has(idx);
      const brandDescription = company.description || "";
      const agencyProfile = hasAgencyAccess ? company.agency_profile : null;
      const hotelBrandVideo = hasAgencyAccess ? company.account_manager_video : null;
      const hotelBrandVideoCorporate = hasAgencyAccess ? company.account_manager_video_corporate : null;
      const agentDownloadDocument = hasAgencyAccess ? company.agency_download_cdn_path : null;
      const accountManagerUpdate = hasAgencyAccess ? company.account_manager_update : null;

      return (
        <Fragment key={`representation-company-${company.id || idx}`}>
          <div>
            <span className="sr-only">Representation Company for {company.name}</span>
            <section className="bg-white text-lg mb-3 shadow" role="region" aria-labelledby={`company-heading-${idx}`}>
              <div className={'flex justify-between'}>
                <div>
                  <h2
                    id={`company-heading-${idx}`}
                    className={`pt-4 px-3 text-xl ${mobile &&
                    "mb-3 py-4 px-3 text-xl border-b border-light-blue-grey flex items-center justify-between cursor-pointer"}`}
                    onClick={mobile ? () => toggleExpanded(idx) : undefined}
                    onKeyPress={mobile ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleExpanded(idx);
                      }
                    } : undefined}
                    tabIndex={mobile ? 0 : undefined}
                    role={mobile ? "button" : undefined}
                    aria-expanded={mobile ? isExpanded : undefined}
                    aria-controls={mobile ? `company-content-${idx}` : undefined}
                  >
                    About {company.name}
                    {mobile && !isExpanded && (
                      <FaRegPlusSquare className="text-base" aria-hidden="true" />
                    )}
                    {mobile && isExpanded && (
                      <FaRegMinusSquare className="text-base" aria-hidden="true" />
                    )}
                  </h2>
                  {brandDescription && (
                    <ContentFeature classes="col px-3 mb-3">
                      <ReadMoreText maxLength={500}>{brandDescription}</ReadMoreText>
                    </ContentFeature>
                  )}
                </div>

                {company && company.cdn_path ?
                  <div className="bg-white px-4 mr-2 mt-2 ">
                    <img
                      src={`${process.env.REACT_APP_API}/api/hotel-groups/${company.id}/image`}
                      style={{
                        maxWidth: 72,
                        maxHeight: 72,
                      }}
                    />
                  </div>
                  : null}
              </div>
              {(!mobile || (mobile && isExpanded)) && (
                <div id={mobile ? `company-content-${idx}` : undefined} className="row">
                  <div className="col col-w-full">

                    <div className="mb-6">
                      {agencyProfile && (isAgency || isRolePlatformAdmin) ? (
                        <ContentFeature title="Agent Information" classes="px-3 mb-3">
                          <ReadMoreText>{agencyProfile}</ReadMoreText>
                        </ContentFeature>
                      ) : null}

                      {agentDownloadDocument && (isAgency || isRolePlatformAdmin) ? (
                        <div className="px-3 mb-3">
                          <a
                            href={agentDownloadDocument}
                            download
                            target="_blank"
                            rel="noreferrer noopener"
                            className="inline-flex items-center py-2 text-sm font-medium transition-colors duration-200"
                            aria-label={`Download document for ${company.name}`}
                          >
                            <ButtonBrand outlined={true} onClick={() => {
                              venueInteraction({
                                type: VENUE_TAGS.VENUE_DOWNLOAD_GROUP_AGENCY_DOCUMENT,
                                data_score: dataScore,
                                impression_id: impressionId,
                                id:venueId,
                              });
                            }}>
                              <FaDownload className="mr-2" aria-hidden="true" />
                              Download Document
                            </ButtonBrand>
                          </a>
                        </div>
                      ) : null}

                      {accountManagerUpdate && (isAgency || isRolePlatformAdmin) ? (
                        <ContentFeature title="Account Manager Update" classes="px-3 mb-3">
                          <ReadMoreText>{accountManagerUpdate}</ReadMoreText>
                        </ContentFeature>
                      ) : null}

                      <div className={'row px-3 mb-3'}>
                        {hotelBrandVideo && (isAgency || isRolePlatformAdmin) ?  (
                          <div className="col col-w-1/2">
                            <div className="relative w-full mb-4"
                                 style={{ paddingBottom: "56.25%" }}>
                              <iframe
                                className="absolute pin w-full h-full"
                                id="ytplayer"
                                src={getYouTubeEmbedUrl(hotelBrandVideo)}
                                frameBorder="0"
                                allowFullScreen
                              />
                            </div>
                          </div>
                        ) : null}
                        {hotelBrandVideo && (isCorporate || isRolePlatformAdmin) ? (
                          <div className="col col-w-1/2">
                            <div className="relative w-full mb-4"
                                 style={{ paddingBottom: "56.25%" }}>
                              <iframe
                                className="absolute pin w-full h-full"
                                id="ytplayer"
                                src={getYouTubeEmbedUrl(hotelBrandVideoCorporate)}
                                frameBorder="0"
                                allowFullScreen
                              />
                            </div>
                          </div>
                        ) : null}

                      </div>
                    </div>
                  </div>
                </div>
              )}
            </section>
          </div>
        </Fragment>
      );
    });
  }

  if (!validCompanies.length) return null;

  return  (
    <div ref={companyRef}>
      {displayCompanies()}
    </div>
  )

};

const mapStateToProps = (state) => ({
  isAgency: isAgency(state),
  isCorporate: isCorporate(state) || isCompanyAdmin(state.auth) || isCompanyUser(state.auth),
  isRolePlatformAdmin: isRolePlatformAdmin(state),
});

export default connect(mapStateToProps, { venueInteraction })(RepresentationCompanies);